import { priorityName, taskStatusName, type Activity, type Member, type TaskStatus } from '@hive/shared';
import { memberName } from './format';

/** Who did it: "Ed", "Claude (for Ed)", or "Hive" for the SQL editor and scripts. */
export function activityActor(entry: Activity, members: readonly Member[]): string {
  if (entry.author_kind === 'system') return 'Hive';
  const actor = memberName(members, entry.actor_id) ?? (entry.author_kind === 'claude' ? 'Claude' : 'Someone');
  const behalf = memberName(members, entry.on_behalf_of);
  return behalf ? `${actor} (for ${behalf})` : actor;
}

type Diff = Record<string, unknown>;

function change(diff: Diff, key: string): [unknown, unknown] | null {
  const v = diff[key];
  return Array.isArray(v) && v.length === 2 ? [v[0], v[1]] : null;
}

/** What happened, in a few words: "moved it to Done, set priority to Urgent". */
export function activitySummary(
  entry: Activity,
  members: readonly Member[],
  sprintName: (id: string | null) => string | null = () => null,
): string {
  const diff = (entry.diff ?? {}) as Diff;
  switch (entry.verb) {
    case 'created':
      return 'created this task';
    case 'imported':
      return `imported this task from ${typeof diff.source === 'string' ? capitalise(diff.source) : 'another tool'}`;
    case 'deleted':
      return 'deleted this task';
    case 'commented':
      return 'commented';
    case 'edited_comment':
      return 'edited a comment';
    case 'deleted_comment':
      return 'deleted a comment';
    case 'labeled':
      return `added label ${typeof diff.label === 'string' ? diff.label : ''}`.trim();
    case 'unlabeled':
      return `removed label ${typeof diff.label === 'string' ? diff.label : ''}`.trim();
    case 'blocked_by':
      return `marked it blocked by ${typeof diff.title === 'string' ? `“${diff.title}”` : 'another task'}`;
    case 'unblocked_by':
      return `removed the block by ${typeof diff.title === 'string' ? `“${diff.title}”` : 'another task'}`;
    case 'updated':
      return describeUpdate(diff, members, sprintName) || 'updated this task';
    default:
      return entry.verb;
  }
}

function describeUpdate(
  diff: Diff,
  members: readonly Member[],
  sprintName: (id: string | null) => string | null,
): string {
  const parts: string[] = [];
  for (const key of Object.keys(diff)) {
    const c = change(diff, key);
    if (!c) continue;
    const [, to] = c;
    switch (key) {
      case 'status':
        parts.push(`moved it to ${taskStatusName(to as TaskStatus)}`);
        break;
      case 'priority':
        parts.push(`set priority to ${priorityName(Number(to))}`);
        break;
      case 'assignee_id':
        parts.push(to ? `assigned it to ${memberName(members, to as string)}` : 'unassigned it');
        break;
      case 'due_date':
        parts.push(to ? `set the due date to ${String(to)}` : 'cleared the due date');
        break;
      case 'start_date':
        parts.push(to ? `set the start date to ${String(to)}` : 'cleared the start date');
        break;
      case 'title':
        parts.push(`renamed it “${String(to)}”`);
        break;
      case 'description':
        parts.push('edited the description');
        break;
      case 'estimate_hours':
        parts.push(to === null ? 'cleared the estimate' : `estimated ${String(to)}h`);
        break;
      case 'game_id':
        parts.push('moved it to another game');
        break;
      case 'parent_id':
        parts.push(to ? 'made it a subtask' : 'made it a top-level task');
        break;
      case 'sprint_id':
        parts.push(to ? `planned it into ${sprintName(to as string) ?? 'a sprint'}` : 'moved it to the backlog');
        break;
      case 'completed_at':
        break;
      default:
        parts.push(`changed ${key.replace(/_/g, ' ')}`);
    }
  }
  return parts.join(', ');
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
